import { useEffect } from "react";
import Lenis from "lenis";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { Navbar } from "@/components/Navbar";
import { Hero } from "@/components/Hero";
import { Services } from "@/components/Services";
import { WhyChooseUs } from "@/components/WhyChooseUs";
import { BeforeAfter } from "@/components/BeforeAfter";
import { Pricing } from "@/components/Pricing";
import { Booking } from "@/components/Booking";
import { Testimonials } from "@/components/Testimonials";
import { Contact } from "@/components/Contact";
import { Footer } from "@/components/Footer";
import { Loader } from "@/components/Loader";
import { ChatWidget } from "@/components/ChatWidget";
import { AuthCallback } from "@/components/AuthCallback";
import { AuthProvider } from "@/lib/auth";
import MembershipPage from "@/pages/MembershipPage";
import AboutPage from "@/pages/AboutPage";
import AdminPage from "@/pages/AdminPage";
import MyBookingsPage from "@/pages/MyBookingsPage";
import AssistantPage from "@/pages/AssistantPage";

function useLenis() {
  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);
}

const scrollToForm = () =>
  document.querySelector("#booking")?.scrollIntoView({ behavior: "smooth" });

function Home() {
  useLenis();
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const id = location.hash;
      setTimeout(() => {
        document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });
      }, 300);
    }
  }, [location.hash]);

  return (
    <div className="App dark grain bg-[#050505]" data-testid="app-root">
      <Navbar onBook={scrollToForm} />
      <main>
        <Hero onBook={scrollToForm} />
        <Services onBook={scrollToForm} />
        <WhyChooseUs onBook={scrollToForm} />
        <BeforeAfter />
        <Pricing onBook={scrollToForm} />
        <Booking />
        <Testimonials />
        <Contact onBook={scrollToForm} />
      </main>
      <Footer />
    </div>
  );
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/memberships" element={<MembershipPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/admin" element={<AdminPage />} />
      <Route path="/my-bookings" element={<MyBookingsPage />} />
      <Route path="/assistant" element={<AssistantPage />} />
    </Routes>
  );
}

function RouteLoader() {
  const location = useLocation();
  return location.pathname === "/" ? <Loader /> : null;
}

function RouteChatWidget() {
  const location = useLocation();
  return location.pathname === "/assistant" ? null : <ChatWidget />;
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <RouteLoader />
        <AppRouter />
        <RouteChatWidget />
        <Toaster position="top-center" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
